import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { INavigationItem } from '../../interfaces/INavigation';
import {
  NeptuneMenuGroup,
  NeptuneMenuItem,
  NeptuneSidenavElement,
} from '../../interfaces/INeptuneSidenav';
import { HttpCrudService } from '../../core/services/http-crud.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @ViewChild('neptuneSidenav')
  private readonly neptuneSidenavRef!: ElementRef<NeptuneSidenavElement>;

  private readonly crudService = inject(HttpCrudService);
  private readonly router = inject(Router);
  public readonly authService = inject(AuthenticationService);
  private readonly destroy$ = new Subject<void>();

  private menuData: NeptuneMenuGroup[] = [];

  /**
   * Fetches navigation items, builds filtered menu data, and initialises the
   * Neptune sidenav once its custom element is defined.
   */
  ngOnInit(): void {
    this.crudService
      .get<INavigationItem[]>('assets/leftnavigationList.json')
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.applyInheritedPermissions(items);
        const currentRoute = this.normalizeRoute(this.router.url);
        this.menuData = this.buildMenuData(items, currentRoute);
        // Wait for the web component to be defined, then push data once
        void customElements.whenDefined('neptune-sidenav').then(() => {
          const sidenav = this.neptuneSidenavRef.nativeElement;
          sidenav.setMenuData?.(this.menuData);
          // Set initial active route; Neptune auto-tracks subsequent navigation via history.pushState
          sidenav.setActiveRoute?.(this.normalizeRoute(this.router.url));
        });
      });
  }

  /**
   * Cancels all active subscriptions.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles the `neptune-sidenav-navigate` event by routing to the selected
   * item and collapsing the sidenav back to rail state.
   *
   * @param event - Custom event carrying the target route in `detail.route`
   */
  onNavigate(event: Event): void {
    const route = (event as CustomEvent<{ route?: string }>).detail?.route;
    if (route) {
      void this.router.navigateByUrl(route);
      this.neptuneSidenavRef.nativeElement.setAttribute('state', 'rail');
    }
  }

  /**
   * Determines whether a navigation item should be visible for the
   * currently logged-in user, based on its effective permissions.
   *
   * Rules:
   * - permissions === undefined/null  -> treat as misconfigured, hide item
   * - permissions.length === 0        -> public item, always visible
   * - otherwise                       -> defer to AuthenticationService
   */
  hasAccess(item: INavigationItem): boolean {
    if (!item || item.permissions == null) {
      return false;
    }

    if (item.permissions.length === 0) {
      return true;
    }

    return this.authService.hasAnyPermission(item.permissions);
  }

  /**
   * Applies permission inheritance down the navigation tree.
   *
   * If a child item does not define `permissions`, it inherits the effective
   * permissions from its nearest ancestor that does, all the way up to the root.
   * This avoids duplicating permissions in the JSON configuration while keeping
   * the runtime permission checks simple.
   *
   * @param items - Current level of navigation items
   * @param parentPermissions - Effective permissions inherited from the parent level
   */
  private applyInheritedPermissions(
    items: INavigationItem[] | undefined,
    parentPermissions?: string[],
  ): void {
    if (!items || items.length === 0) {
      return;
    }

    items.forEach((item) => {
      const hasOwnPermissions = typeof item.permissions !== 'undefined';
      const effectivePermissions = hasOwnPermissions
        ? item.permissions
        : parentPermissions;

      if (!hasOwnPermissions && effectivePermissions) {
        item.permissions = effectivePermissions;
      }

      if (item.subMenu && item.subMenu.length > 0) {
        this.applyInheritedPermissions(item.subMenu, effectivePermissions);
      }
    });
  }

  /**
   * Converts a flat list of navigation items into Neptune menu groups,
   * filtering out items the current user cannot access.
   *
   * @param items - Top-level navigation items from the JSON config
   * @param currentRoute - Normalised current URL used to pre-mark the active item
   */
  private buildMenuData(
    items: INavigationItem[],
    currentRoute = '',
  ): NeptuneMenuGroup[] {
    const menu = items
      .map((item) => this.mapItem(item, currentRoute, true))
      .filter((item): item is NeptuneMenuItem => item !== null);
    return menu.length > 0 ? [{ menu }] : [];
  }

  /**
   * Recursively maps a single navigation item to the Neptune menu item shape.
   * Returns `null` when the item is inaccessible or produces no valid route.
   *
   * @param item - Navigation item to map
   * @param currentRoute - Normalised current URL
   * @param topLevel - True for top-level items; controls whether `isActive` is embedded
   */
  private mapItem(
    item: INavigationItem,
    currentRoute: string,
    topLevel: boolean,
  ): NeptuneMenuItem | null {
    if (!this.hasAccess(item)) return null;

    const subMenu = (item.subMenu ?? [])
      .map((child) => this.mapItem(child, currentRoute, false))
      .filter((c): c is NeptuneMenuItem => c !== null);

    if (!item.route && subMenu.length === 0) return null;

    const route = item.route
      ? this.normalizeRoute(item.route)
      : this.normalizeRoute(subMenu[0]?.route ?? '');

    if (!route) return null;

    // Only top-level items carry isActive; Neptune's #M() uses this.#l for submenu children
    const isActive =
      topLevel &&
      !!currentRoute &&
      (route === currentRoute || subMenu.some((c) => c.route === currentRoute));

    return {
      name: item.name,
      route,
      icon: item.icon ?? 'fa-regular fa-circle',
      ...(isActive ? { isActive: true } : {}),
      ...(subMenu.length > 0 ? { subMenu } : {}),
    };
  }

  /**
   * Strips query strings, fragments, and trailing slashes from a route string
   * and ensures it starts with a leading slash.
   *
   * @param route - Raw route value to normalise
   * @returns Normalised path, or an empty string when the input is falsy
   */
  private normalizeRoute(route: string | null | undefined): string {
    if (!route) {
      return '';
    }

    const [path] = route.split(/[?#]/);
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return normalizedPath !== '/'
      ? normalizedPath.replace(/\/+$/, '')
      : normalizedPath;
  }
}
